import _createForOfIteratorHelper from "@babel/runtime/helpers/esm/createForOfIteratorHelper";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _asyncToGenerator from "@babel/runtime/helpers/esm/asyncToGenerator";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import { isDefined, isString } from '@dateam/ark';
import React from 'react';
import { Navigate as ReactRouterNavigate } from 'react-router-dom';
import { GuardContext } from './contexts';
import { useRouterRegistration } from './hooks';
import NavigationRoute from './navigationRoute';
import { GuardTypes } from './types';

/**
 ** Memoized callback to get the next callback function used in guards.
 ** Assigns the `props` and `redirect` functions to callback.
 **/

var getNextFn = function getNextFn(resolve) {
  // eslint-disable-line @typescript-eslint/ban-types
  var next = function next() {
    resolve({
      type: GuardTypes.CONTINUE
    });
  };
  var abort = function abort(reason) {
    resolve({
      type: GuardTypes.ABORT,
      payload: reason
    });
  };
  var redirect = function redirect(to) {
    resolve({
      type: GuardTypes.REDIRECT,
      payload: to
    });
  };
  return Object.assign(next, {
    abort: abort,
    redirect: redirect
  });
};
var runGuard = function runGuard(key, guard, metadata) {
  return new Promise( /*#__PURE__*/function () {
    var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(resolve, reject) {
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            try {
              // TODO: Bind 'from' route
              guard(getNextFn(resolve), new NavigationRoute(key).addMetadata(metadata), new NavigationRoute(''));
            } catch (error) {
              reject(error);
            }
          case 1:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }));
    return function (_x, _x2) {
      return _ref.apply(this, arguments);
    };
  }());
};
var Route = function Route(_ref2) {
  var path = _ref2.path,
    component = _ref2.component,
    options = _ref2.options,
    _ref2$metadata = _ref2.metadata,
    metadata = _ref2$metadata === void 0 ? {} : _ref2$metadata;
  var _useRouterRegistratio = useRouterRegistration(),
    _useRouterRegistratio2 = _slicedToArray(_useRouterRegistratio, 1),
    router = _useRouterRegistratio2[0];
  var guards = React.useContext(GuardContext);
  // const [isValidated, setIsValidated] = React.useState<boolean>(false);
  var _React$useState = React.useState(null),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    redirect = _React$useState2[0],
    setRedirect = _React$useState2[1];
  var _ref3 = options !== null && options !== void 0 ? options : {},
    key = _ref3.key,
    Layout = _ref3.layout,
    _ref3$layoutData = _ref3.layoutData,
    layoutData = _ref3$layoutData === void 0 ? {} : _ref3$layoutData;
  var resolveGuards = React.useCallback( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
    var _iterator, _step, guard, action;
    return _regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) switch (_context2.prev = _context2.next) {
        case 0:
          // eslint-disable-next-line no-restricted-syntax
          _iterator = _createForOfIteratorHelper(guards);
          _context2.prev = 1;
          _iterator.s();
        case 3:
          if ((_step = _iterator.n()).done) {
            _context2.next = 12;
            break;
          }
          guard = _step.value;
          _context2.next = 7;
          return runGuard(key, guard, metadata);
        case 7:
          action = _context2.sent;
          if (!(action.type === GuardTypes.ABORT || action.type === GuardTypes.REDIRECT)) {
            _context2.next = 10;
            break;
          }
          return _context2.abrupt("return", action);
        case 10:
          _context2.next = 3;
          break;
        case 12:
          _context2.next = 17;
          break;
        case 14:
          _context2.prev = 14;
          _context2.t0 = _context2["catch"](1);
          _iterator.e(_context2.t0);
        case 17:
          _context2.prev = 17;
          _iterator.f();
          return _context2.finish(17);
        case 20:
          return _context2.abrupt("return", {
            type: GuardTypes.CONTINUE
          });
        case 21:
        case "end":
          return _context2.stop();
      }
    }, _callee2, null, [[1, 14, 17, 20]]);
  })), [key, guards, metadata]);

  /**
   ** Validates the route using the guards. If an error occurs, it
   ** will toggle the route error state.
   **/
  var validateRoute = React.useCallback( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3() {
    var action, _path;
    return _regeneratorRuntime.wrap(function _callee3$(_context3) {
      while (1) switch (_context3.prev = _context3.next) {
        case 0:
          _context3.prev = 0;
          _context3.next = 3;
          return resolveGuards();
        case 3:
          action = _context3.sent;
          if (action.type === GuardTypes.REDIRECT) {
            if (isDefined(router)) {
              _path = action.payload.toPath(router);
              if (_path != null) setRedirect(_path);
            }
          } else if (action.type === GuardTypes.CONTINUE) {
            // setIsValidated(true);
          }
          _context3.next = 10;
          break;
        case 7:
          _context3.prev = 7;
          _context3.t0 = _context3["catch"](0);
          console.warn('ERROR ...', _context3.t0);

          // TODO: Handle error
          // routeError = error.message || 'Not found.';
        case 10:
        case "end":
          return _context3.stop();
      }
    }, _callee3, null, [[0, 7]]);
  })), [router, resolveGuards]);
  React.useEffect(function () {
    var redirect = options === null || options === void 0 ? void 0 : options.redirect;
    if (isString(redirect)) {
      setRedirect(redirect);
    } else {
      validateRoute();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (isString(redirect)) {
    return /*#__PURE__*/React.createElement(ReactRouterNavigate, {
      to: redirect
    });
  }
  var Component = component;
  if (!isDefined(Component)) return null;
  // if (!isValidated) return null;

  if (isDefined(Layout)) {
    return (
      /*#__PURE__*/
      // <ReactRouterRoute
      //     path={path}
      //     element={(
      React.createElement(Layout, {
        data: layoutData
      }, /*#__PURE__*/React.createElement(Component, {
        metadata: metadata
      }))
      // )}
      // />
    );
  }
  return (
    /*#__PURE__*/
    // <ReactRouterRoute
    // path={path}
    // element={(
    React.createElement(Component, {
      metadata: metadata
    })
    // )}
    // />
  );
};
export default Route;